<template>
  <div
    class="bg-gradient-to-br from-spicy-mustard-100 to-mocha-mousse-50 backdrop-blur-md rounded-2xl shadow-sm p-3 md:p-6"
  >
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between mb-6">
      <div class="flex items-center gap-2 font-semibold">
        <div class="relative group flex items-center gap-2">
          <Icon
            icon="mdi:account-multiple"
            class="text-mocha-mousse-600 text-xl"
          />
          <span>Uploader(s)</span>

          <!-- Info Icon with Tooltip -->
          <div class="relative">
            <Icon
              icon="mdi:information-outline"
              class="text-gray-400 hover:text-mocha-mousse cursor-pointer text-lg transition"
            />
            <div
              class="absolute hidden group-hover:block left-1/2 -translate-x-1/2 top-6 z-20 bg-gray-800 text-white text-xs rounded-lg px-3 py-2 whitespace-normal w-64 shadow-lg"
            >
              The first name associated with this archive indicates the person
              who uploaded it, not necessarily the main author, curator, or
              principal investigator.
            </div>
          </div>
        </div>
      </div>

      <!-- Search bar -->
      <div class="flex items-center gap-2 mt-3 sm:mt-0 w-full sm:w-1/2">
        <p class="text-sm text-gray-700">Search:</p>
        <div ref="dropdownRef" class="relative w-full">
          <input
            type="text"
            id="authors"
            placeholder="Type a name..."
            class="block border border-gray-300 rounded-xl focus:ring-2 focus:ring-mocha-mousse-500 w-full px-3 py-1.5 text-sm"
            v-model="query"
            @focus="isDropdownOpen = true"
            @input="searchAuthors"
          />

          <!-- Dropdown -->
          <div
            v-if="isDropdownOpen"
            id="authors-suggestion"
            class="absolute left-0 right-0 mt-1 max-h-50 border border-gray-200 bg-white rounded-xl shadow-lg overflow-auto z-99"
          >
            <template v-for="(user, index) in possible_authors" :key="index">
              <div
                v-if="user.show"
                class="cursor-pointer px-3 py-2 hover:bg-gradient-to-r hover:from-arabesque-50 hover:to-spicy-mustard-100 border-b border-gray-100 transition"
                @click="addUploader($event, user)"
              >
                <p class="font-medium text-gray-700">
                  {{ user.name }} {{ user.lastname }}
                </p>
                <p class="text-xs text-gray-500">{{ user.institute }}</p>
              </div>
            </template>

            <div
              v-if="
                query.length >= query_limit && possible_authors.length === 0
              "
              class="p-3 text-gray-500 text-sm"
            >
              No authors found
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Selected Uploaders -->
    <div id="selected-authors" class="flex flex-wrap gap-3">
      <template v-for="(uploader, index) in formState.uploaders" :key="index">
        <div
          class="flex items-center gap-2 border border-mocha-mousse-200 rounded-full px-3 py-1 bg-gradient-to-r from-arabesque-100 to-spicy-mustard-200 hover:bg-gradient-to-r hover:from-arabesque-200 hover:to-spicy-mustard-300 transition-colors cursor-move group relative"
          :draggable="index !== 0"
          @dragstart="onDragStart(index)"
          @dragover.prevent
          @drop="onDrop(index)"
        >
          <!-- Avatar Icon -->
          <Icon
            icon="mdi:account-circle"
            class="text-mocha-mousse-800 text-lg"
          />

          <!-- Name -->
          <span class="text-sm">
            {{ uploader.name }} {{ uploader.lastname }}
          </span>

          <!-- Remove Icon -->
          <div class="relative">
            <Icon
              v-if="index !== 0"
              icon="mdi:close-circle-outline"
              class="text-red-500 hover:text-red-700 cursor-pointer text-lg transition"
              @click="removeUploader(uploader)"
            />
            <div
              v-if="index !== 0"
              class="absolute hidden group-hover:block left-1/2 -translate-x-1/2 top-6 z-20 bg-gray-800 text-white text-xs rounded-lg px-2 py-1 shadow-lg"
            >
              Remove uploader
            </div>
          </div>

          <!-- Tooltip for first uploader -->
          <div
            v-if="index === 0"
            class="absolute hidden group-hover:block left-1/2 -translate-x-1/2 top-8 z-20 bg-gray-800 text-white text-xs rounded-lg px-2 py-1 shadow-lg"
          >
            Primary uploader (you)
          </div>
        </div>
      </template>
    </div>

    <!-- Validation Error -->
    <div
      v-if="uploadersAreEmpty() && formChecked"
      class="text-red-500 text-xs flex items-center gap-2 mt-3"
    >
      <Icon icon="mdi:alert-circle-outline" class="text-base" />
      <span>Insert at least one uploader</span>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import { Icon } from "@iconify/vue";
import { useFormStore } from "../stores/FormStore";
import apiClient from "../services/api";
import authService from "@/services/authService";
import { useLogger } from "@/stores/useLogger";

const logger = useLogger();
logger.wireLifecycle();

const formStore = useFormStore();
const formState = formStore.formState;

const query_limit = 0;
const query = ref("");
const isDropdownOpen = ref(false);
const dropdownRef = ref(null);
const possible_authors = ref([]);
const formChecked = ref(false);
let draggedItemIndex = null;

onMounted(() => {
  document.addEventListener("click", handleGlobalClick);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleGlobalClick);
});

let debounceTimer;

const searchAuthors = (ev) => {
  const value = ev?.target?.value ?? "";
  logger.log("search_author", { metadata: { search_query: value } });

  if (value.length >= query_limit) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(async () => {
      try {
        const response = await apiClient.getUsers(value);
        if (response.data.result === "OK") {
          console.log(response.data.message);
          possible_authors.value = response.data.message.map((item) => ({
            id: item.id,
            name: item.name,
            lastname: item.lastname,
            email: item.email,
            institute: item.institute,
            show: true,
          }));
        } else {
          possible_authors.value = [];
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        possible_authors.value = [];
      }
      refreshPossibleAuthors();
    }, 400);
  } else {
    clearTimeout(debounceTimer);
    possible_authors.value = [];
    refreshPossibleAuthors();
  }
};

const addUploader = (event, user) => {
  event.preventDefault();
  // Avoid duplicates: if already present, ignore
  if (!formState.uploaders.some((u) => u.id === user.id)) {
    formStore.addUploader(user);
  }
  isDropdownOpen.value = false;
  refreshPossibleAuthors();
  logger.log("add_uploader", {
    metadata: { curr_uploaders: formState.uploaders },
  });
};

const removeUploader = (user) => {
  const index = formState.uploaders.findIndex((u) => u.id === user.id);
  if (index !== -1) {
    formStore.removeUploader(index);
  }
  refreshPossibleAuthors();
  logger.log("remove_uploader", {
    metadata: { curr_uploaders: formState.uploaders },
  });
};

const handleGlobalClick = (e) => {
  if (dropdownRef.value && !dropdownRef.value.contains(e.target)) {
    isDropdownOpen.value = false;
  }
};

const refreshPossibleAuthors = () => {
  const ids = formState.uploaders.map((el) => el.id);
  possible_authors.value = possible_authors.value.map((el) => ({
    ...el,
    show: !ids.includes(el.id),
  }));
};

const onDragStart = (index) => {
  draggedItemIndex = index;
};

const onDrop = (dropIndex) => {
  if (draggedItemIndex === null) return;

  // Prevent dropping into 0 if your rule disallows moving leader
  if (dropIndex === 0) dropIndex = 1;

  const list = [...formState.uploaders];
  const [dragged] = list.splice(draggedItemIndex, 1);
  list.splice(dropIndex, 0, dragged);

  formStore.setUploaders(list); // ← commit via store
  draggedItemIndex = null;

  logger.log("change_uploaders_order", {
    metadata: { curr_uploaders: formState.uploaders },
  });
};

const uploadersAreEmpty = () => formState?.uploaders.length === 0;

const firstUploaderIsWrong = () => {
  const user = authService.currentUser.value;
  return (
    formState?.uploaders.length > 0 &&
    user &&
    formState?.uploaders[0].id != user.vuo_id
  );
};

const validateInput = () => {
  formChecked.value = true;
  let isValid = true;
  if (uploadersAreEmpty() || firstUploaderIsWrong()) {
    isValid = false;
  }
  return isValid;
};

defineExpose({
  validateInput,
});
</script>
